export function getPageNumbers(currentPage, pageCount) {
  const total = Math.max(0, Number(pageCount) || 0)
  const current = Math.min(Math.max(1, Number(currentPage) || 1), total || 1)

  if (total <= 9) {
    return Array.from({ length: total }, (_, index) => index + 1)
  }

  const result = [1]
  if (current > 4) result.push('...')
  const start = Math.max(2, current - 2)
  const end = Math.min(total - 1, current + 2)
  for (let page = start; page <= end; page += 1) result.push(page)
  if (current < total - 3) result.push('...')
  result.push(total)
  return result
}

export function paginateRows(rows, currentPage, pageSize) {
  const size = Math.max(1, Number(pageSize) || 1)
  const start = (Math.max(1, Number(currentPage) || 1) - 1) * size
  return (Array.isArray(rows) ? rows : []).slice(start, start + size)
}

function numberValue(value) {
  const parsed = Number.parseFloat(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const PRICE_BANDS = [
  { name: '经济型 <250', min: Number.NEGATIVE_INFINITY, max: 250 },
  { name: '中端 250-400', min: 250, max: 400 },
  { name: '中高端 400-500', min: 400, max: 500 },
  { name: '高端 ≥500', min: 500, max: Number.POSITIVE_INFINITY },
]

export function buildPriceBands(hotels) {
  const bands = PRICE_BANDS.map(({ name }) => ({
    name,
    hotel_cnt: 0,
    orders: 0,
    sales: 0,
  }))

  const source = Array.isArray(hotels) ? hotels : []
  source.forEach((hotel) => {
    if (
      !hotel ||
      hotel.adr === null ||
      hotel.adr === undefined ||
      hotel.adr === ''
    ) {
      return
    }

    const adr = Number.parseFloat(hotel.adr)
    if (!Number.isFinite(adr)) return

    const bandIndex = PRICE_BANDS.findIndex(
      ({ min, max }) => adr >= min && adr < max,
    )
    if (bandIndex < 0) return

    bands[bandIndex].hotel_cnt += 1
    bands[bandIndex].orders += numberValue(hotel.orders)
    bands[bandIndex].sales += numberValue(hotel.revenue ?? hotel.sales)
  })

  return bands
}

export function getPriceBandValues(bands, metric) {
  const field = metric === 'revenue' ? 'sales' : 'orders'
  return (Array.isArray(bands) ? bands : []).map((band) =>
    numberValue(band?.[field]),
  )
}

export function formatPriceBandLabel(value, metric) {
  const number = numberValue(value)
  if (number === 0) return ''
  return metric === 'revenue'
    ? `${(number / 10000).toFixed(1)}万`
    : `${number.toLocaleString('zh-CN')} 单`
}

function firstArray(candidates) {
  return candidates.find(Array.isArray)
}

function getNested(source, path) {
  return path.reduce(
    (value, key) =>
      value && typeof value === 'object' ? value[key] : undefined,
    source,
  )
}

export function buildPieSeries(items, metric, allowMetricValue = false) {
  const isRevenue = metric === 'revenue'
  return (Array.isArray(items) ? items : []).map((item) => {
    const datum = item && typeof item === 'object' ? item : {}
    return {
      ...datum,
      value: numberValue(
        isRevenue
          ? datum.sales ??
              datum.revenue ??
              datum.amount ??
              (allowMetricValue ? datum.value : undefined)
          : datum.orders ?? datum.order_num ?? datum.value,
      ),
    }
  })
}

export function getDimensionPieSeries(dimensions, dimension, metric) {
  const source = dimensions && typeof dimensions === 'object' ? dimensions : {}
  const base = source[dimension]
  const metricNames =
    metric === 'revenue' ? ['sales', 'revenue', 'amount'] : ['orders', 'order']
  const alternatives = []
  metricNames.forEach((name) => {
    alternatives.push(source[`${dimension}_${name}`], getNested(base, [name]))
  })
  const metricItems = firstArray(alternatives)
  if (metricItems) return buildPieSeries(metricItems, metric, true)
  return buildPieSeries(base, metric)
}

export function getHourlySeries(dimensions, day, metric) {
  const source = dimensions && typeof dimensions === 'object' ? dimensions : {}
  const hourly = source.hourly
  const isYesterday = day === 'yesterday'
  const isRevenue = metric === 'revenue'
  const dayName = isYesterday ? 'yesterday' : 'today'
  const metricNames = isRevenue
    ? ['sales', 'revenue', 'amount']
    : ['orders', 'order', 'order_num', 'value']
  const candidates = []

  metricNames.forEach((metricName) => {
    candidates.push(
      source[`${dayName}_hourly_${metricName}`],
      source[`hourly_${dayName}_${metricName}`],
      getNested(hourly, [dayName, metricName]),
      getNested(hourly, [`${dayName}_${metricName}`]),
    )
  })

  if (!isYesterday) {
    metricNames.forEach((metricName) => {
      candidates.push(
        source[`hourly_${metricName}`],
        getNested(hourly, [metricName]),
      )
    })
  }

  if (!isRevenue) {
    candidates.push(
      source[`${dayName}_hourly`],
      source[`hourly_${dayName}`],
      getNested(hourly, [dayName]),
    )
  }

  if (!isYesterday && !isRevenue) candidates.push(hourly)

  let values = firstArray(candidates)
  if (!values && Array.isArray(hourly) && hourly.some((item) => item && typeof item === 'object')) {
    values = hourly.map((item) => {
      const dayValue = item[dayName]
      if (dayValue && typeof dayValue === 'object') {
        const value = metricNames.map((name) => dayValue[name]).find((v) => v !== undefined)
        return numberValue(value)
      }
      const value = metricNames
        .map((name) => item[`${dayName}_${name}`] ?? (!isYesterday ? item[name] : undefined))
        .find((v) => v !== undefined)
      return numberValue(value)
    })
  }

  return Array.from({ length: 24 }, (_, index) => numberValue(values?.[index]))
}

export function getHourlyChartSeries(dimensions, historyData, day, metric) {
  if (day !== 'history') return getHourlySeries(dimensions, day, metric)

  const values = historyData?.[metric]
  return Array.from({ length: 24 }, (_, index) => numberValue(values?.[index]))
}

export function formatChartMetric(value, metric) {
  const number = numberValue(value)
  if (metric === 'revenue') {
    return `¥${number.toLocaleString('zh-CN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} 元`
  }
  return `${number.toLocaleString('zh-CN')} 单`
}

export function getHourlyChartTitle(day, metric) {
  const dayLabel =
    day === 'yesterday' ? '昨日' : day === 'history' ? '历史' : '今日'
  return `${dayLabel}分时${
    metric === 'revenue' ? '营业额' : '下单'
  }`
}
